/**
 * The flashcard maker's parser, exports, shuffle and duplex print layout.
 */

export type CardSeparator = "auto" | "tab" | "dash" | "colon" | "comma";

export interface Flashcard {
  term: string;
  definition: string;
}

export interface ParsedDeck {
  cards: Flashcard[];
  /** 1-based numbers of non-empty lines that had no usable separator. */
  skipped: number[];
}

const LINE_BREAK = /\r\n|\r|\n/;
/** A hyphen or dash with whitespace either side, so "e-mail" is never split. */
const DASH = /\s+[-–—]\s+/;
const FIELD_BREAKS = /[\t\r\n]+/g;
const QUOTE = /"/g;

interface Cut {
  at: number;
  length: number;
}

function findCut(line: string, separator: Exclude<CardSeparator, "auto">) {
  if (separator === "dash") {
    const match = DASH.exec(line);
    return match ? { at: match.index, length: match[0].length } : null;
  }
  const token = { tab: "\t", colon: ":", comma: "," }[separator];
  const at = line.indexOf(token);
  return at === -1 ? null : { at, length: token.length };
}

/**
 * Auto-detect, per line: a tab always wins (it is what spreadsheets and Quizlet
 * exports use); otherwise whichever of " - " and ":" comes first; a comma only when
 * neither is there, because commas turn up inside terms ("Paris, France") far more
 * often than dashes or colons do.
 */
function autoCut(line: string): Cut | null {
  const tab = findCut(line, "tab");
  if (tab) {
    return tab;
  }
  const dash = findCut(line, "dash");
  const colon = findCut(line, "colon");
  if (dash && colon) {
    return dash.at < colon.at ? dash : colon;
  }
  return dash ?? colon ?? findCut(line, "comma");
}

/** One card per line: term, separator, definition. Blank lines are ignored. */
export function parseFlashcards(
  text: string,
  separator: CardSeparator
): ParsedDeck {
  const cards: Flashcard[] = [];
  const skipped: number[] = [];
  for (const [index, line] of text.split(LINE_BREAK).entries()) {
    if (line.trim() === "") {
      continue;
    }
    const cut = separator === "auto" ? autoCut(line) : findCut(line, separator);
    const term = cut ? line.slice(0, cut.at).trim() : "";
    const definition = cut ? line.slice(cut.at + cut.length).trim() : "";
    if (term && definition) {
      cards.push({ term, definition });
    } else {
      skipped.push(index + 1);
    }
  }
  return { cards, skipped };
}

function flatten(value: string): string {
  return value.replace(FIELD_BREAKS, " ").trim();
}

/** Quizlet's import box: one card per line, a tab between term and definition. */
export function toQuizletText(cards: readonly Flashcard[]): string {
  return cards
    .map((card) => `${flatten(card.term)}\t${flatten(card.definition)}`)
    .join("\n");
}

/**
 * Anki's text import, with the file headers Anki 2.1.54+ reads (`#separator`, `#html`).
 *
 * Anki parses the rows as tab-separated CSV, so a field containing a double quote is
 * quoted with the inner quotes doubled — and so is a field starting with `#`, which
 * Anki would otherwise read as a comment line.
 */
export function toAnkiText(cards: readonly Flashcard[]): string {
  const field = (value: string) => {
    const flat = flatten(value);
    return flat.includes('"') || flat.startsWith("#")
      ? `"${flat.replace(QUOTE, '""')}"`
      : flat;
  };
  const rows = cards.map(
    (card) => `${field(card.term)}\t${field(card.definition)}`
  );
  return `${["#separator:tab", "#html:false", ...rows].join("\n")}\n`;
}

/** An unbiased integer in [0, max) from the browser's CSPRNG. Event handlers only. */
export function cryptoRandomBelow(max: number): number {
  const limit = 0x1_00_00_00_00 - (0x1_00_00_00_00 % max);
  const buffer = new Uint32Array(1);
  let value = limit;
  while (value >= limit) {
    crypto.getRandomValues(buffer);
    value = buffer[0];
  }
  return value % max;
}

/** Fisher–Yates over a copy. `randomBelow(n)` must return an integer in [0, n). */
export function shuffled<T>(
  items: readonly T[],
  randomBelow: (max: number) => number
): T[] {
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const other = randomBelow(index + 1);
    [copy[index], copy[other]] = [copy[other], copy[index]];
  }
  return copy;
}

/** Split a list into pages of `perPage`. */
export function paginate<T>(items: readonly T[], perPage: number): T[][] {
  const pages: T[][] = [];
  for (let index = 0; index < items.length; index += perPage) {
    pages.push(items.slice(index, index + perPage));
  }
  return pages;
}

export type DuplexFlip = "long" | "short";

/**
 * Which front cell each back cell sits behind, for double-sided printing.
 *
 * Cells are numbered row by row from the top left. Turning a portrait sheet over its
 * long edge swaps left and right, so the backs mirror the columns; turning it over its
 * short edge swaps top and bottom, so the backs mirror the rows.
 */
export function backCellOrder(
  columns: number,
  rows: number,
  flip: DuplexFlip
): number[] {
  const order: number[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      order.push(
        flip === "long"
          ? row * columns + (columns - 1 - column)
          : (rows - 1 - row) * columns + column
      );
    }
  }
  return order;
}
